/* Guard de subidas en vuelo: una recarga automática NO puede matar bytes que el
 * operador ya está subiendo.
 *
 * POR QUÉ EXISTE. Orca recarga la pestaña sola en tres sitios, y ninguno miraba
 * si había una subida en curso:
 *   1. `index.js` — `controllerchange` tras `skipWaiting()` del SW nuevo. El SW
 *      llama a `skipWaiting()` en su propio `install` (`public/sw.js`), así que
 *      esta recarga NO la decide el usuario: llega sola tras un deploy.
 *   2. `ChunkErrorBoundary` — un `import()` que falla porque el build cambió.
 *   3. `UpdateBanner` — ahí sí la pide el usuario, pero merece avisarle. Desde
 *      que las actualizaciones son siempre explícitas, (1) ya NO recarga y (2)
 *      y (3) esperan sin techo tras un clic del operador.
 *
 * Medido en producción el 2026-09-18: cuatro `POST /api/uploads` seguidos de
 * Adrián (00:01, 00:03, 00:06, 00:09 CEST) salieron **400 con 0 bytes de
 * respuesta** en el nginx del host y no llegaron NI al nginx del compose ni al
 * backend. Ese es el rastro exacto de un cliente que cierra la conexión a mitad
 * del cuerpo: nginx cierra el registro con 400 y no proxea nada. El servidor no
 * se tocó entre esos 400 y el 200 de las 07:40 — ni un reload de nginx, ni un
 * reinicio de contenedor. Lo único que cambió fue la pestaña.
 *
 * QUÉ CUENTA COMO SUBIDA. Cualquier petición cuyo cuerpo sea `FormData`. Es la
 * definición honesta de "hay bytes del usuario viajando", y cubre los DOCE
 * llamantes que hoy hacen `POST .../uploads` sin tener que mantener una lista de
 * rutas que se queda obsoleta al siguiente componente que suba algo.
 *
 * Y el guard EXPLÍCITO (`beginUpload`) existe para lo que el interceptor no
 * puede ver: una operación de varios pasos. Subir un asset son DOS peticiones
 * —`/uploads` y luego `/byteplus/assets/register`— y una recarga en el hueco
 * entre ambas deja el objeto huérfano en Spaces sin fila que lo reclame.
 *
 * Módulo puro y sin dependencias a propósito: el gate corre los `*.test.mjs` con
 * node ANTES del `npm ci`. `installUploadTracking` RECIBE la instancia de axios;
 * no la importa.
 */

// Techo del aplazamiento. Una petición colgada no puede dejar la pestaña pegada
// para siempre a un build viejo cuyos chunks ya devuelven 404: pasado el techo
// se recarga igual. 15 min cubre con holgura el `timeout` más largo que usa un
// uploader hoy (20 min es el de vídeo largo, pero ese va por `register-long`,
// que hace polling y libera el guard en cuanto el job es durable).
export const DEFER_CEILING_MS = 15 * 60 * 1000;

let inFlight = 0;
let waiters = [];

/** Cuántas subidas hay ahora mismo en vuelo. */
export function uploadsInFlight() {
  return inFlight;
}

export function hasUploadsInFlight() {
  return inFlight > 0;
}

function drain() {
  if (inFlight > 0) return;
  const pending = waiters;
  waiters = [];
  pending.forEach((w) => {
    if (w.timer) clearTimeout(w.timer);
    try { w.run("idle"); } catch (_) { /* un waiter roto no bloquea a los demás */ }
  });
}

/**
 * Marca el inicio de una subida. Devuelve la función que la cierra.
 * Idempotente: llamar dos veces al finalizador NO descuenta dos veces, que es
 * como el contador se iría a negativo y dejaría de proteger nada.
 */
export function beginUpload() {
  inFlight++;
  let done = false;
  return function endUpload() {
    if (done) return;
    done = true;
    inFlight = Math.max(0, inFlight - 1);
    drain();
  };
}

/**
 * Ejecuta `action` ya mismo si no hay subidas en vuelo; si las hay, la aplaza
 * hasta que terminen. `action` recibe el motivo: "idle" (terminaron) o
 * "ceiling" (se agotó la espera). Devuelve true si se ejecutó en el acto.
 *
 * `ceilingMs: Infinity` = espera SIN techo (la actualización que pide el
 * usuario nunca debe tirar una subida). OJO: `setTimeout(fn, Infinity)` NO
 * espera para siempre —el motor lo trata como 0 y dispara al instante—, por eso
 * el techo infinito no arma ningún temporizador.
 */
export function deferWhileUploading(action, { ceilingMs = DEFER_CEILING_MS, setTimer = setTimeout } = {}) {
  if (!hasUploadsInFlight()) {
    action("now");
    return true;
  }
  const waiter = { run: action, timer: null };
  if (Number.isFinite(ceilingMs)) {
    waiter.timer = setTimer(() => {
      waiters = waiters.filter((w) => w !== waiter);
      try { action("ceiling"); } catch (_) { /* ignore */ }
    }, ceilingMs);
  }
  waiters.push(waiter);
  return false;
}

/**
 * Un cuerpo `FormData` es la señal de que viajan bytes del usuario. No se mira
 * la URL: la lista de rutas de subida cambia cada vez que alguien añade un
 * componente, y una lista desactualizada es un guard que no guarda.
 */
export function isUploadRequest(config) {
  if (!config) return false;
  const method = String(config.method || "get").toLowerCase();
  if (method !== "post" && method !== "put" && method !== "patch") return false;
  const body = config.data;
  if (!body) return false;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  // jsdom/node y algunos polyfills no comparten el constructor global.
  return String(body?.constructor?.name || "") === "FormData";
}

/**
 * Cablea el contador a una instancia de axios. Todo POST con FormData queda
 * contado de principio a fin, pase lo que pase: se libera igual en éxito que en
 * error, porque una subida que falla tampoco debe dejar el contador clavado.
 */
export function installUploadTracking(axiosInstance) {
  if (!axiosInstance?.interceptors) return () => {};
  const reqId = axiosInstance.interceptors.request.use((config) => {
    if (isUploadRequest(config)) {
      // El finalizador viaja en el propio config para sobrevivir a reintentos y
      // redirecciones: el interceptor de respuesta lo encuentra ahí.
      config.__orcaEndUpload = beginUpload();
    }
    return config;
  });
  const resId = axiosInstance.interceptors.response.use(
    (response) => {
      response?.config?.__orcaEndUpload?.();
      return response;
    },
    (error) => {
      error?.config?.__orcaEndUpload?.();
      return Promise.reject(error);
    },
  );
  return () => {
    axiosInstance.interceptors.request.eject(reqId);
    axiosInstance.interceptors.response.eject(resId);
  };
}

/** Solo para tests: devuelve el contador a cero. */
export function _resetUploadsInFlight() {
  inFlight = 0;
  waiters.forEach((w) => w.timer && clearTimeout(w.timer));
  waiters = [];
}
